import type { AllowablePartType, PartRule } from '../../hooks/useGetSettings';

/**
 * Shared value helpers for part allowance rules. Both the Add/Edit part modal
 * and the inline cells on the Part Allowances table read and write the same
 * three fields (part types, makes, prohibited insurance), so the conversion
 * and labelling rules live here rather than being duplicated per surface.
 */

/**
 * Part types as the picker models them: the stored `allowablePartTypes` array
 * splits aftermarket into one entry per certification, which is awkward to
 * tick off, so the UI works in flags and converts on the way out.
 */
export interface DraftPartTypes {
    am: boolean;
    amNonCertified: boolean;
    amCAPA: boolean;
    amKeysIQ: boolean;
    reconReman: boolean;
    lkq: boolean;
}

export const defaultPartTypes: DraftPartTypes = {
    am: false,
    amNonCertified: false,
    amCAPA: false,
    amKeysIQ: false,
    reconReman: false,
    lkq: false,
};

export function draftToAllowable(pt: DraftPartTypes): AllowablePartType[] {
    const result: AllowablePartType[] = [];
    if (pt.lkq) result.push({ partType: 'rcy' });
    if (pt.reconReman) {
        result.push({ partType: 're-man' });
        result.push({ partType: 're-con' });
    }
    if (pt.am) {
        const allOn = pt.amNonCertified && pt.amCAPA && pt.amKeysIQ;
        const noneOn = !pt.amNonCertified && !pt.amCAPA && !pt.amKeysIQ;
        if (allOn || noneOn) {
            result.push({ partType: 'a/m' });
        } else {
            if (pt.amCAPA) result.push({ partType: 'a/m', certifications: ['capa'] });
            if (pt.amKeysIQ && !pt.amCAPA) result.push({ partType: 'a/m', certifications: ['keysiq'] });
            if (pt.amNonCertified) result.push({ partType: 'a/m' });
        }
    }
    return result;
}

export function allowableToDraft(types: AllowablePartType[]): DraftPartTypes {
    const pt: DraftPartTypes = { ...defaultPartTypes };
    for (const t of types ?? []) {
        if (t.partType === 'rcy') pt.lkq = true;
        if (t.partType === 're-man' || t.partType === 're-con') pt.reconReman = true;
        if (t.partType === 'a/m') {
            pt.am = true;
            if (!t.certifications?.length) {
                pt.amNonCertified = true;
                pt.amCAPA = true;
                pt.amKeysIQ = true;
            } else {
                for (const c of t.certifications) {
                    if (c === 'capa') pt.amCAPA = true;
                    if (c === 'keysiq') pt.amKeysIQ = true;
                }
            }
        }
    }
    return pt;
}

/** True when no type at all is ticked, which the table reads as "All types". */
export const isEmptyPartTypes = (pt: DraftPartTypes): boolean =>
    !pt.am && !pt.reconReman && !pt.lkq;

export const isSamePartTypes = (a: DraftPartTypes, b: DraftPartTypes): boolean =>
    a.am === b.am &&
    a.amNonCertified === b.amNonCertified &&
    a.amCAPA === b.amCAPA &&
    a.amKeysIQ === b.amKeysIQ &&
    a.reconReman === b.reconReman &&
    a.lkq === b.lkq;

/**
 * The cell label: "A/M (CAPA), LKQ" and so on. Aftermarket names its
 * certifications only when it is restricted to some of them — an unrestricted
 * A/M entry would otherwise read as three near-identical chips.
 */
export const formatPartTypes = (pt: DraftPartTypes): string => {
    const labels: string[] = [];
    if (pt.am) {
        const certs: string[] = [];
        if (pt.amNonCertified) certs.push('Non-Certified');
        if (pt.amCAPA) certs.push('CAPA');
        if (pt.amKeysIQ) certs.push('KeysIQ');
        const isUnrestricted = certs.length === 0 || certs.length === 3;
        labels.push(isUnrestricted ? 'A/M' : `A/M (${certs.join(', ')})`);
    }
    if (pt.reconReman) labels.push('Recon/Reman');
    if (pt.lkq) labels.push('LKQ');
    return labels.length > 0 ? labels.join(', ') : 'All types';
};

export type RuleMakes = 'all' | Array<{ _id: string; name: string; }>;

export const normalizeRuleMakes = (
    makes: PartRule['makes'],
    oemOptions: Array<{ _id: string; name: string; }>,
): RuleMakes => {
    if (!makes || makes === 'all' || !Array.isArray(makes) || makes.length === 0) {
        return 'all';
    }
    const makeNameMap = new Map(oemOptions.map((make) => [make._id, make.name]));
    const normalized = makes
        .map((make) => {
            const makeId = make._id?.toString();
            if (!makeId) return null;
            return {
                _id: makeId,
                name: make.name || makeNameMap.get(makeId) || 'Unknown',
            };
        })
        .filter((make): make is { _id: string; name: string; } => !!make);
    return normalized.length > 0 ? normalized : 'all';
};

export const makeScopeLabel = (makes: RuleMakes) => {
    if (makes === 'all') return 'All OEMs';
    if (makes.length === 0) return 'All OEMs';
    const names = makes.map((make) => make.name);
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
};

export const isSameRuleMakes = (a: RuleMakes, b: RuleMakes): boolean => {
    if (a === 'all' || b === 'all') return a === b;
    if (a.length !== b.length) return false;
    const other = new Set(b.map((make) => make._id));
    return a.every((make) => other.has(make._id));
};

export type RuleInsurance = undefined | 'all' | Array<{ _id: string; name: string; }>;

export const normalizeProhibitedInsurance = (
    insurance: PartRule['prohibitedInsurance'],
): RuleInsurance => {
    if (insurance === undefined) return undefined;
    if (insurance === 'all') return 'all';
    if (!Array.isArray(insurance) || insurance.length === 0) return undefined;
    const normalized = insurance
        .map((i) => ({ _id: i._id?.toString() ?? '', name: i.name ?? 'Unknown' }))
        .filter((i) => !!i._id);
    return normalized.length > 0 ? normalized : undefined;
};

export const insuranceScopeLabel = (insurance: RuleInsurance) => {
    if (insurance === undefined) return 'Not Set';
    if (insurance === 'all') return 'All Insurance';
    if (insurance.length === 0) return 'Not Set';
    const names = insurance.map((i) => i.name);
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
};

export const isSameRuleInsurance = (a: RuleInsurance, b: RuleInsurance): boolean => {
    if (a === undefined || b === undefined) return a === b;
    if (a === 'all' || b === 'all') return a === b;
    if (a.length !== b.length) return false;
    const other = new Set(b.map((entry) => entry._id));
    return a.every((entry) => other.has(entry._id));
};
